//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

(function () {
  const curalate = angular.module('curalate');

  curalate.service('ContentLibraryApiService', [
    '$http',
    '$upload',
    'CollectionFiltersConverter',
    'PromiseService',
    'RestBaseUrl',
    'SessionVarsService',
    function (
      $http,
      $upload,
      CollectionFiltersConverter,
      PromiseService,
      RestBaseUrl,
      SessionVarsService,
    ) {
      // All things are actually lists of strings. This is a bad DTO pattern, so they need to be turned into the correct types.
      // The two options are either booleans, or true values.
      const cleanFilterArrayList = (param) => {
        return param && param.length > 0 ? param : undefined;
      };

      const cleanFilterArrayValue = (param) => {
        if (_.isArray(param)) {
          return param && param.length > 0 ? param[0] : undefined;
        } else {
          return param;
        }
      };

      const cleanFilterArrayBoolean = (param) => {
        if (_.isArray(param)) {
          return param && param.length > 0 ? param[0] === 'true' : undefined;
        } else if (_.isString(param)) {
          return param ? param === 'true' : undefined;
        } else {
          return param;
        }
      };

      const transformFlatParamsToNested = (params) => {
        const trackedBusinessUserIds = params.trackedBusinessUserIds || [];
        const trackedCompetitorIds = params.trackedCompetitorIds || [];
        const transformedParams = {
          filters: {
            isScheduled: cleanFilterArrayBoolean(params.isScheduled),
            orientation: cleanFilterArrayList(params.orientation),
            mediaItemTypes: cleanFilterArrayList(params.mediaItemTypes),
            distribution: cleanFilterArrayList(params.distribution),
            isDistributed: cleanFilterArrayBoolean(params.isDistributed),
            rightsStatuses: cleanFilterArrayList(params.rightsStatuses),
            hasProducts: cleanFilterArrayBoolean(params.hasProducts),
            labels: cleanFilterArrayList(params.labels),
            campaignTypes: cleanFilterArrayList(params.campaignTypes),
            itemTypes: cleanFilterArrayList(params.itemTypes),
            itemId: cleanFilterArrayValue(params.itemId),
            userIds: cleanFilterArrayList(params.userIds),
            height: {
              min: cleanFilterArrayValue(params.minHeight),
              max: cleanFilterArrayValue(params.maxHeight),
            },
            width: {
              min: cleanFilterArrayValue(params.minWidth),
              max: cleanFilterArrayValue(params.maxWidth),
            },
            aspectRatio: {
              min: cleanFilterArrayValue(params.minAspectRatio),
              max: cleanFilterArrayValue(params.maxAspectRatio),
            },
            duplicateItemExclusions: {
              excludeMatchedMisItemForInstagramPost: cleanFilterArrayBoolean(
                params.excludeMatchedMisItemForInstagramPost,
              ),
              excludeOriginalRepostedInstagramItem: cleanFilterArrayBoolean(
                params.excludeOriginalRepostedInstagramItem,
              ),
              excludeMatchedInstagramItemForInstagramStory:
                cleanFilterArrayBoolean(
                  params.excludeMatchedInstagramItemForInstagramStory,
                ),
            },
            spamFiltering: cleanFilterArrayBoolean(params.spamFiltering),
            createdAfter: cleanFilterArrayValue(params.createdAfter),
            igUsernames: cleanFilterArrayList(params.igUsername),
            igHashtags: cleanFilterArrayList(params.igHashtag),
            filter: cleanFilterArrayValue(params.filter),
            searchText: cleanFilterArrayValue(params.searchText),
            dateRange: cleanFilterArrayValue(
              CollectionFiltersConverter.parseDatePickerFilterObject(
                _(params).safeGet('dateRange.0'),
              ),
            ),
            semanticTags: cleanFilterArrayList(params.semanticTags),
            experimentTags: params.experimentTags,
            visualScore: cleanFilterArrayValue(params.visualScore),
            visualSearchDetection: cleanFilterArrayList(
              params.visualSearchDetection,
            ),
            excludedContentSources: cleanFilterArrayList(
              params.excludedContentSources,
            ),
            reelModerationStatus: {
              status: cleanFilterArrayValue(params.moderationStatus),
            },
            minuteOffsetRange: params.minuteOffsetRange,
            instagramMentionedUsernames: cleanFilterArrayList(
              params.instagramMentionedUsernames || params.mentionsMyBrand,
            ),
            tiktokMentionedUsernames: cleanFilterArrayList(
              params.tiktokMentionedUsernames,
            ),
            platforms: cleanFilterArrayList(params.platforms),
          },
          sources: {
            // 'brandFeedPost' and 'brandStoryPost' are Boolean fields, but we store them as Arrays and push a dummy
            // value onto them to signify when we want them to be defined, so here we check that they are non-empty
            brandFeedPost: cleanFilterArrayBoolean(params.brandFeedPost),
            brandStoryPost: cleanFilterArrayBoolean(params.brandStoryPost),
            brandTikTokPosts: cleanFilterArrayList(params.brandTikTokPosts),
            collectionId: cleanFilterArrayValue(params.collectionId),
            curalateUserIds: cleanFilterArrayList(params.curalateUserIds),
            galleryUploaders: cleanFilterArrayList(params.galleryUploaders),
            // If the override connectVendor param was provided, use that. Otherwise use the collection's
            // default shareCreatorClients
            shareCreatorClients: cleanFilterArrayList(
              params.connectVendor || params.shareCreatorClients,
            ),
            trackedUserIds: cleanFilterArrayList(params.trackedUserIds),
            trackedBusinessUserIds: cleanFilterArrayList(
              trackedBusinessUserIds.concat(trackedCompetitorIds),
            ),
            trackedHashtags: cleanFilterArrayList(params.trackedHashtags),
            accountMentionIds: cleanFilterArrayList(params.accountMentionIds),
            tiktokMentionedUserIds: cleanFilterArrayList(
              params.tiktokMentionedUserIds || params.mentionsMyBrand,
            ),
            pinterestUserIds: cleanFilterArrayList(params.pinterestUserIds),
            sourcesGroups: cleanFilterArrayList(params.sourcesGroups),
            contentGroup: cleanFilterArrayValue(params.contentGroup),
          },
          // If we're searching for items in the streams drawer, hydrate the
          // results with additional data from DTS (e.g. labels, products)
          hydrateWithDeetsItems: params.hydrateWithDeetsItems,
          itemIds: cleanFilterArrayList(params.itemIds),
          limit: cleanFilterArrayValue(params.limit),
          offset: cleanFilterArrayValue(params.offset),
          orderBy: cleanFilterArrayValue(params.orderBy),
        };

        return transformedParams;
      };
      /**
       * Get all Content Items.
       * @param {Object} params to filter Content Items returned. Includes offset, filter, and limit.
       * @return {Object} Returns a promise.
       */
      this.getContentItemsAsync = function (params) {
        const transformedParams = transformFlatParamsToNested(params);
        return PromiseService.processHttpResponse(
          $http
            .post(
              `${CURALATE_ENV.WEB_GATEWAY_URL}/api/v1/items/search`,
              transformedParams,
            )
            .then((resp) => resp.data),
        );
      };

      this.transformParams = function (params) {
        return transformFlatParamsToNested(params);
      };

      /**
       * Get autocomplete suggestions for hashtags and usernames based on a prefix and set of filters
       * @param {String} autocompleteType One of the values defined in `AutocompleteTypes`
       * @param {String} query The autocomplete suggestions to search for. If no search is desired, pass an empty string
       * @param {Object} params to filter what labels are shown for the current collection
       * @return {Object} Returns a promise containing autocomplete suggestions.
       */
      this.getAutocompleteSuggestionsAsync = function (
        autocompleteType,
        query,
        params,
      ) {
        const transformedParams = transformFlatParamsToNested(params);
        const autocompleteParams = {
          ...transformedParams,
          autocompleteType,
          prefix: query,
        };

        return PromiseService.processHttpResponse(
          $http.post(
            `${RestBaseUrl}/content-library/autocomplete`,
            autocompleteParams,
          ),
        );
      };

      /**
       * Get autocomplete suggestions for visual tags based on prefix and set of filters
       * @param {String} autocompleteType One of the values defined in `AutocompleteTypes`
       * @param {String} query The autocomplete suggestions to search for. If no search is desired, pass an empty string
       * @param {Object} params to filter what labels are shown for the current collection
       * @return {Object} Returns a promise containing autocomplete suggestions.
       */
      this.getVisualTagsSuggestionsAsync = function (
        aggregateType,
        query,
        params,
      ) {
        const transformedParams = transformFlatParamsToNested(params);
        const aggregateParams = {
          ...transformedParams,
          type: aggregateType,
          sortByDistanceTo: query,
        };

        return PromiseService.processHttpResponse(
          $http.post(
            `${RestBaseUrl}/content-library/aggregate`,
            aggregateParams,
          ),
        );
      };

      /**
       * Delete a single MIS item.
       * @param {String} itemId of the item to delete.
       * @return {Object} Returns a promise.
       */
      this.removeContentItemAsync = function (itemId) {
        return PromiseService.processHttpResponse(
          $http.delete(`${RestBaseUrl}/content-library/items/${itemId}`),
        );
      };

      this.getItemDetailsAsync = function (itemIds) {
        const bffBaseUrl = SessionVarsService.getBaseUrl('web_gateway');

        const url = `${bffBaseUrl}/api/v1/item-details`;

        return PromiseService.processHttpResponse(
          $http.get(url, {
            params: { itemIds },
          }),
        );
      };

      this.getContentLibrarySettingsAsync = function () {
        return PromiseService.processHttpResponse(
          $http.get(`${RestBaseUrl}/content-library/settings`),
        );
      };

      this.saveContentLibrarySettingsAsync = function (newSettings) {
        return PromiseService.processHttpResponse(
          $http.put(`${RestBaseUrl}/content-library/settings`, newSettings),
        );
      };

      this.getCollectionExportUrl = function (collectionId) {
        return `${RestBaseUrl}/content-library/export-collection/${collectionId}/zip`;
      };
    },
  ]);
})();
